import { useMemo, useState } from 'react';
import { FileBarChart } from 'lucide-react';
import { usePnlQuery } from '@/client/api/business/spend';
import { REVENUE_LINES } from '@/client/types/business/spend';
import { AccountingPageShell } from './components/AccountingPageShell';
import { filterClass } from './components/spend-ui';
import { ReportBody, shiftMonth, thisMonth } from './components/report-ui';
import { EmptyState } from '../HumanResource/components/hr-ui';
import { money } from '../HumanResource/components/comp-format';

const lineLabel = (k: string) => REVENUE_LINES.find((l) => l.id === k)?.label ?? k;

/** Profit & loss: earned vs incurred, per month. */
export default function ProfitLossPage() {
  const [span, setSpan] = useState(12);
  const to = thisMonth();
  const from = shiftMonth(to, -(span - 1));
  const q = usePnlQuery(from, to);
  const currencies = useMemo(() => [...new Set((q.data?.rows ?? []).filter((r) => r.totals.income || r.totals.costs).map((r) => r.currency))].sort(), [q.data]);
  const [picked, setPicked] = useState<string | null>(null);
  const currency = picked && currencies.includes(picked) ? picked : currencies[0] ?? 'GHS';
  const t = q.data?.totals.find((x) => x.currency === currency);
  const margin = t && t.income ? (t.net / t.income) * 100 : null;

  return (
    <AccountingPageShell
      mock={false}
      title="Profit & loss"
      description="Income when it was earned (revenue net of refunds) against costs when they were incurred: approved bills and expense claims by category, and payroll (salaries and employer SSNIT) for the month paid for."
      crumbs={[{ label: 'Accounting', href: '/admin/accounting' }, { label: 'Reports' }, { label: 'Profit & loss' }]}
      icon={FileBarChart}
      actions={
        <div className="flex gap-2">
          <select value={span} onChange={(e) => setSpan(Number(e.target.value))} className={filterClass} aria-label="Range">
            {[3, 6, 12].map((n) => (
              <option key={n} value={n}>
                Last {n} months
              </option>
            ))}
          </select>
          {currencies.length > 1 && (
            <select value={currency} onChange={(e) => setPicked(e.target.value)} className={filterClass} aria-label="Currency">
              {currencies.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          )}
        </div>
      }
      stats={[
        { key: 'income', label: 'Income', value: t ? money(t.income, currency) : '—' },
        { key: 'costs', label: 'Costs', value: t ? money(t.costs, currency) : '—' },
        { key: 'net', label: 'Profit', value: t ? money(t.net, currency) : '—' },
        { key: 'margin', label: 'Margin', value: margin === null ? '—' : `${margin.toFixed(0)}%` },
      ]}
    >
      {q.isError ? (
        <EmptyState label={`Could not load the report: ${(q.error as Error)?.message ?? 'unknown error'}`} />
      ) : q.isLoading || !q.data ? (
        <EmptyState label="Working out profit & loss…" />
      ) : (
        <ReportBody report={q.data} a="income" b="costs" labels={['Income', 'Costs']} currency={currency} lineLabel={lineLabel} />
      )}
    </AccountingPageShell>
  );
}
